import React, { useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Chip,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import { RestaurantMenu, WarningAmber, Close, OpenInFull, DragIndicator } from "@mui/icons-material";

import { useMenuBuilder } from "../context/MenuBuilderContext";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import {
  groupSelectedItemsByCategory,
  buildSavePayload,
  getExceededLimits,
} from "../utils";
import {
  IExceededLimit,
} from "../types/menuBuilder.types";
import { IMenuBuilderOutput } from "../../shared/types/foodPayload.types";
import { LimitOverrideDialog } from "./LimitOverrideDialog";
import { IStoreState } from "../../../../redux/initialStoreState";
import { IMenuJSON } from "../../../../redux/food/food.types";
import { useBillingCompanyContext } from "../../../../providers";
import { MENU_BUILDER_TOKENS } from "../designTokens";
import { MuiNumberField } from "../../../../components/formsComponents/MuiNumberField";

const { colors, borderRadius, typography, spacing } = MENU_BUILDER_TOKENS;

// ─── Props ──────────────────────────────────────────────────────────────────────

interface IOrderSummaryPanelProps {
  mode: "master" | "lead";
  onSave: (data: any) => void;
  onPreview?: (previewData: any) => void;
  leadUuid?: string;
  masterMenuUuid?: string;
  masterMenuName?: string;
  masterMenuPrice?: string | null;
  addedLeadMenuUuid?: string;
  saving?: boolean;
  expanded?: boolean;
  tags?: string[];
  imageUrl?: string | null;
  saveButtonLabel?: string;
}

// ─── Component ──────────────────────────────────────────────────────────────────

export const OrderSummaryPanel: React.FC<IOrderSummaryPanelProps> = ({
  mode,
  onSave,
  onPreview,
  leadUuid,
  masterMenuUuid,
  masterMenuName,
  masterMenuPrice,
  addedLeadMenuUuid,
  saving = false,
  expanded = false,
  tags,
  imageUrl,
  saveButtonLabel,
}) => {
  const {
    selectedItems,
    categoryLimits,
    itemVisibility,
    visibilityEditMode,
    menuTitle,
    menuPrice,
    setMenuTitle,
    setMenuPrice,
    removeItem,
    setCategoryLimit,
    selectedMasterMenu,
    selectCategory,
    isItemVisible,
    categoryOrder,
    reorderCategory,
    setCategoryOrder,
  } = useMenuBuilder();

  const allCategories = useSelector(
    (state: IStoreState) => state.food.foodCategory.data
  );

  const { comapny } = useBillingCompanyContext();
  const companyOutletUuid = comapny?.company_outlet_uuid ?? "";

  const [exceededLimitsDialogOpen, setExceededLimitsDialogOpen] = useState(false);
  const [exceededLimits, setExceededLimits] = useState<IExceededLimit[]>([]);

  const masterMenuData: IMenuJSON[] = useMemo(() => {
    return allCategories.map((cat) => ({
      category_uuid: cat.category_uuid ?? "",
      category_name: cat.category_name,
      food_items: cat.food_items || [],
      subcategories: cat.subcategories || [],
      max_selection: categoryLimits[cat.category_uuid ?? ""] || undefined,
    }));
  }, [allCategories, categoryLimits]);

  const groupedItems = useMemo(
    () => {
      const groups = groupSelectedItemsByCategory(selectedItems, masterMenuData);
      // Sort by categoryOrder if available
      if (categoryOrder.length > 0) {
        groups.sort((a, b) => {
          const idxA = categoryOrder.indexOf(a.categoryId);
          const idxB = categoryOrder.indexOf(b.categoryId);
          return (idxA === -1 ? Infinity : idxA) - (idxB === -1 ? Infinity : idxB);
        });
      }
      return groups;
    },
    [selectedItems, masterMenuData, categoryOrder]
  );

  const totalCategoryCount = groupedItems.length;
  const totalItemCount = selectedItems.length;

  // ─── Save Logic ─────────────────────────────────────────────────────────────

  const executeSave = () => {
    // Build menu_json using the existing buildSavePayload utility
    const legacyPayload = buildSavePayload({
      mode,
      selectedItems,
      categoryLimits,
      itemVisibility,
      isFixed: visibilityEditMode,
      allCategories,
      title: menuTitle,
      description: "",
      price: mode === "master" ? menuPrice : null,
      leadUuid: leadUuid ?? null,
      masterMenuUuid: masterMenuUuid ?? null,
      masterMenuName: masterMenuName ?? null,
      masterMenuPrice: masterMenuPrice ?? null,
      addedLeadMenuUuid: addedLeadMenuUuid ?? null,
      companyOutletUuid: companyOutletUuid || null,
      tags: tags ?? [],
      categoryOrder,
    });

    // Always emit IMenuBuilderOutput — consumers handle payload building
    const output: IMenuBuilderOutput = {
      selectedItems,
      menuJson: legacyPayload.menu_json,
      categoryLimits,
      itemVisibility,
      isFixed: visibilityEditMode,
    };
    onSave(output);
  };

  const handleSave = () => {
    const exceeded = getExceededLimits(selectedItems, categoryLimits, allCategories);
    if (exceeded.length > 0) {
      setExceededLimits(exceeded);
      setExceededLimitsDialogOpen(true);
      return;
    }
    executeSave();
  };

  const handleLimitOverrideConfirm = () => {
    setExceededLimitsDialogOpen(false);
    executeSave();
  };

  const handleLimitOverrideCancel = useCallback(() => {
    setExceededLimitsDialogOpen(false);
    setExceededLimits([]);
  }, []);

  const handleDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) return;
    const { source, destination } = result;
    if (source.index === destination.index) return;

    const newOrder = groupedItems.map((g) => g.categoryId);
    const [moved] = newOrder.splice(source.index, 1);
    newOrder.splice(destination.index, 0, moved);
    setCategoryOrder(newOrder);
  }, [groupedItems, setCategoryOrder]);

  const isEditMode = !!(masterMenuUuid || addedLeadMenuUuid || (mode === "lead" && selectedItems.length > 0));
  const saveButtonText = saveButtonLabel
    ? saveButtonLabel
    : mode === "master"
      ? (masterMenuUuid ? "Update Menu" : "Create Menu")
      : (isEditMode ? "Save Menu" : "Create Menu");

  // ─── Empty State ────────────────────────────────────────────────────────────

  if (selectedItems.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: 300,
          p: spacing.panelPadding,
          bgcolor: colors.panelBg,
          borderRadius: borderRadius.panel,
        }}
      >
        <RestaurantMenu sx={{ fontSize: 48, color: colors.textMuted, mb: 2 }} />
        <Typography sx={{ ...typography.body, color: colors.textSecondary }}>
          No items added
        </Typography>
        <Typography sx={{ ...typography.caption, color: colors.textMuted, mt: 0.5 }}>
          Select items from the menu to build your order
        </Typography>
      </Box>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        bgcolor: colors.panelBg,
        borderLeft: `1px solid ${colors.divider}`,
      }}
    >
      {/* Header Section */}
      <Box sx={{ px: 1, pt: 1, pb: 0.75, borderBottom: `1px solid ${colors.divider}`, display: "flex", alignItems: "center", gap: 1 }}>
        {expanded && (
          <IconButton
            size="small"
            onClick={() => {
              // Select first category that has visible items (for fixed menus in lead mode)
              const firstVisibleCategory = mode === "lead"
                ? allCategories.find((cat) => {
                    let visibleCount = 0;
                    if (cat.food_items) {
                      visibleCount += cat.food_items.filter((item) => isItemVisible(item.food_item_uuid ?? "")).length;
                    }
                    if (cat.subcategories) {
                      for (const sub of cat.subcategories) {
                        if (sub.food_items) {
                          visibleCount += sub.food_items.filter((item) => isItemVisible(item.food_item_uuid ?? "")).length;
                        }
                      }
                    }
                    return visibleCount > 0;
                  })
                : allCategories[0];
              if (firstVisibleCategory) {
                selectCategory(firstVisibleCategory);
              }
            }}
            sx={{ color: colors.textSecondary, width: 28, height: 28 }}
          >
            <OpenInFull sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        <Typography sx={{ ...typography.heading, fontSize: "18px", color: colors.textPrimary }}>
          Summary
        </Typography>
      </Box>

      {/* Scrollable grouped items */}
      <Box sx={{ flex: 1, overflow: "auto", px: 1, py: 1 }}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="category-order" isDropDisabled={mode !== "lead"}>
            {(droppableProvided) => (
              <Box
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
                sx={{
                  display: expanded ? "flex" : "block",
                  flexWrap: expanded ? "wrap" : undefined,
                  gap: expanded ? 2 : 0,
                }}
              >
        {groupedItems.map((categoryGroup, groupIndex) => {
          const categoryLimit = categoryLimits[categoryGroup.categoryId];
          const isCategoryOverLimit = categoryLimit > 0 && categoryGroup.totalItems > categoryLimit;

          return (
            <Draggable
              key={categoryGroup.categoryId}
              draggableId={categoryGroup.categoryId}
              index={groupIndex}
              isDragDisabled={mode !== "lead" || groupedItems.length <= 1}
            >
              {(draggableProvided, snapshot) => (
            <Box
              ref={draggableProvided.innerRef}
              {...draggableProvided.draggableProps}
              sx={{
                mb: expanded ? 0 : 1.5,
                borderRadius: "14px",
                border: `1px solid ${isCategoryOverLimit ? "#fca5a5" : colors.divider}`,
                bgcolor: snapshot.isDragging ? "#F0F7FF" : colors.cardBg,
                overflow: "hidden",
                boxShadow: snapshot.isDragging ? "0 8px 24px rgba(0,0,0,0.12)" : "0 1px 4px rgba(0,0,0,0.04)",
                flex: expanded ? "1 1 300px" : undefined,
                maxWidth: expanded ? "400px" : undefined,
              }}
            >
              {/* Category Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 1.25,
                  borderBottom: `1px solid ${colors.divider}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {mode === "lead" && groupedItems.length > 1 && (
                    <Box {...draggableProvided.dragHandleProps} sx={{ display: "flex", alignItems: "center", cursor: "grab", color: colors.textMuted, "&:hover": { color: colors.primaryBlue } }}>
                      <DragIndicator sx={{ fontSize: 18 }} />
                    </Box>
                  )}
                  <Typography sx={{ fontWeight: 700, fontSize: "16px", color: colors.textPrimary }}>
                    {categoryGroup.categoryName}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box
                    sx={{
                      bgcolor: colors.primaryBlueLight,
                      color: colors.primaryBlue,
                      borderRadius: borderRadius.badge,
                      px: 1.5,
                      py: 0.4,
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.03em" }}>
                      {categoryGroup.totalSubcategories} SUB · {categoryGroup.totalItems} ITEMS
                    </Typography>
                  </Box>
                  {isCategoryOverLimit && (
                    <Chip
                      icon={<WarningAmber sx={{ fontSize: 14 }} />}
                      label={`${categoryGroup.totalItems}/${categoryLimit}`}
                      size="small"
                      color="error"
                      sx={{ height: 22, fontSize: "0.7rem", "& .MuiChip-icon": { fontSize: 14 } }}
                    />
                  )}
                  {!isCategoryOverLimit && categoryLimit > 0 && categoryGroup.totalItems >= categoryLimit && (
                    <Chip
                      label={`${categoryGroup.totalItems}/${categoryLimit}`}
                      size="small"
                      color="warning"
                      sx={{ height: 22, fontSize: "0.7rem" }}
                    />
                  )}
                </Box>
              </Box>

              {/* Content area — each subcategory gets its own left border */}
              <Box
                sx={{
                  ml: 1.5,
                  mr: 1.5,
                  my: 1,
                }}
              >
                {categoryGroup.subcategories.map((subcategoryGroup, idx) => {
                  const subLimit = categoryLimits[subcategoryGroup.subcategoryId] || 0;
                  const isSubLimitExceeded = subLimit > 0 && subcategoryGroup.totalItems > subLimit;

                  return (
                    <Box
                      key={subcategoryGroup.subcategoryId}
                      sx={{
                        mb: idx < categoryGroup.subcategories.length - 1 ? 1.5 : 0,
                        borderLeft: `3px solid ${isSubLimitExceeded ? "#ef4444" : colors.subcategoryBorder}`,
                        pl: 0,
                        bgcolor: isSubLimitExceeded ? "#fef2f2" : "transparent",
                        borderRadius: isSubLimitExceeded ? "0 8px 8px 0" : 0,
                      }}
                    >
                      {/* Subcategory Row */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          pl: 2,
                          pr: 0.5,
                          mb: 1,
                        }}
                      >
                        <Typography sx={{ fontWeight: 600, fontSize: "14px", color: colors.textPrimary }}>
                          {subcategoryGroup.subcategoryName}
                        </Typography>
                        <Typography sx={{ ...typography.uppercaseLabel, color: colors.textMuted }}>
                          {subcategoryGroup.totalItems} {subcategoryGroup.totalItems === 1 ? "ITEM" : "ITEMS"}
                        </Typography>
                      </Box>

                      {/* Item list */}
                      <Box sx={{ pl: 2 }}>
                        {subcategoryGroup.items.map((item) => (
                          <Box
                            key={(item as any)._selectionKey ?? item.food_item_uuid}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              py: 0.5,
                              px: 0.5,
                              borderRadius: "8px",
                              "&:hover": {
                                bgcolor: "#F8FAFC",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: colors.bulletDot,
                                mr: 1.5,
                                flexShrink: 0,
                              }}
                            />
                            <Typography sx={{ fontSize: "15px", color: colors.textPrimary, flex: 1 }}>
                              {item.item_name}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => removeItem((item as any)._selectionKey ?? item.food_item_uuid ?? "")}
                              sx={{
                                color: colors.textMuted,
                                width: 24,
                                height: 24,
                                "&:hover": { color: "#ef4444" },
                              }}
                            >
                              <Close sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
              )}
            </Draggable>
          );
        })}
        {droppableProvided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Box>

      {/* Bottom Section */}
      <Divider sx={{ borderColor: colors.divider }} />
      <Box sx={{ p: 1 }}>
        {/* Category/item counts */}
        {(() => {
          const exceeded = getExceededLimits(selectedItems, categoryLimits, allCategories);
          const totalExtra = exceeded.reduce((sum, e) => sum + (e.current - e.max), 0);
          const hasExceeded = totalExtra > 0;

          return (
            <>
              <Typography sx={{ fontSize: "12px", color: hasExceeded ? "#DC2626" : colors.textSecondary, fontWeight: hasExceeded ? 600 : 400, mb: 0.5 }}>
                {totalCategoryCount} {totalCategoryCount === 1 ? "category" : "categories"} · {totalItemCount} {totalItemCount === 1 ? "item" : "items"}
                {hasExceeded && ` (${totalExtra} extra)`}
              </Typography>
              {/* Warning text — right below category count */}
              {hasExceeded && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
                  <WarningAmber sx={{ fontSize: 14, color: "#DC2626" }} />
                  <Typography sx={{ fontSize: "11px", color: "#DC2626", fontWeight: 600 }}>
                    Extra items will be charged additionally
                  </Typography>
                </Box>
              )}
            </>
          );
        })()}

        {/* Price row (lead mode only) */}
        {mode === "lead" && (selectedMasterMenu?.price || menuPrice) > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mb: 1,
              px: 1.5,
              py: 0.75,
              bgcolor: "#F0F9FF",
              borderRadius: "10px",
              border: "1px solid #BAE6FD",
            }}
          >
            <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#0F172A" }}>
              ₹ {selectedMasterMenu?.price || menuPrice}
              <Typography component="span" sx={{ fontSize: "12px", fontWeight: 500, color: "#64748B", ml: 0.25 }}>
                / person
              </Typography>
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 1, mt: 1, maxWidth: 320, ml: "auto" }}>
          {onPreview && (
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={() => {
                // Build a lookup of item_icon from allCategories (source of truth)
                const iconLookup: Record<string, string> = {};
                for (const cat of allCategories) {
                  if (cat.food_items) {
                    for (const fi of cat.food_items) {
                      if (fi.food_item_uuid && fi.item_icon) {
                        iconLookup[fi.food_item_uuid] = fi.item_icon;
                      }
                    }
                  }
                  if (cat.subcategories) {
                    for (const sub of cat.subcategories) {
                      if (sub.food_items) {
                        for (const fi of sub.food_items) {
                          if (fi.food_item_uuid && fi.item_icon) {
                            iconLookup[fi.food_item_uuid] = fi.item_icon;
                          }
                        }
                      }
                    }
                  }
                }

                // Determine the placed category of an item from its _selectionKey
                const itemPlacedCategory = (item: any): string =>
                  typeof item._selectionKey === "string"
                    ? item._selectionKey.split("|")[0]
                    : item.category_list?.[0]?.category_uuid ?? "";

                // Build preview data from current state, respecting category order
                const filteredCategories = masterMenuData.filter((cat) =>
                  selectedItems.some((item) => itemPlacedCategory(item) === cat.category_uuid)
                ).map((cat) => ({
                  ...cat,
                  food_items: selectedItems.filter((item) =>
                    itemPlacedCategory(item) === cat.category_uuid
                  ).map((item) => ({
                    ...item,
                    item_icon: item.item_icon || iconLookup[item.food_item_uuid ?? ""] || null,
                  })),
                }));

                // Sort by categoryOrder
                if (categoryOrder.length > 0) {
                  filteredCategories.sort((a, b) => {
                    const idxA = categoryOrder.indexOf(a.category_uuid);
                    const idxB = categoryOrder.indexOf(b.category_uuid);
                    return (idxA === -1 ? Infinity : idxA) - (idxB === -1 ? Infinity : idxB);
                  });
                }

                const previewMenuData = {
                  menu: {
                    title: menuTitle,
                    description: "",
                    categories: filteredCategories,
                  },
                };
                onPreview(previewMenuData);
              }}
              sx={{
                borderRadius: borderRadius.categoryRow,
                textTransform: "none",
                fontWeight: 600,
                py: 0.75,
                fontSize: "13px",
              }}
            >
              Preview
            </Button>
          )}

          <Button
            variant="contained"
            size="small"
            fullWidth
            onClick={handleSave}
            disabled={saving}
            sx={{
              bgcolor: colors.primaryBlue,
              borderRadius: borderRadius.categoryRow,
              textTransform: "none",
              fontWeight: 600,
              py: 0.75,
              fontSize: "13px",
              "&:hover": { bgcolor: "#1a97f5" },
            }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : saveButtonText}
          </Button>
        </Box>
      </Box>

      <LimitOverrideDialog
        open={exceededLimitsDialogOpen}
        exceededLimits={exceededLimits}
        onConfirm={handleLimitOverrideConfirm}
        onCancel={handleLimitOverrideCancel}
      />
    </Box>
  );
};
